import type { ReactNode } from "react";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface SettingsSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Buttons of the fixed footer; the primary one last. */
  footer?: ReactNode;
}

/** Right-hand sheet with a fixed header and footer: only the form scrolls. */
export function SettingsSheet({ open, onClose, title, description, children, footer }: SettingsSheetProps) {
  return (
    <Sheet open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <SheetContent side="right" className="flex h-full w-full flex-col gap-0 border-l border-border/60 bg-background p-0 sm:max-w-lg">
        <SheetHeader className="shrink-0 space-y-1 border-b border-border/60 px-5 pb-4 pt-6 text-left sm:px-6">
          <SheetTitle className="pr-8 text-lg">{title}</SheetTitle>
          {description ? <SheetDescription className="text-sm leading-6">{description}</SheetDescription> : null}
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">{children}</div>
        {footer ? (
          <SheetFooter className="shrink-0 flex-row items-center justify-end gap-2 border-t border-border/60 px-5 py-3 sm:space-x-0 sm:px-6">
            {footer}
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

/** Label above a control, with an optional hint under it. */
export function SheetField({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      {children}
      {hint ? <p className="text-xs leading-5 text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
